import * as React from 'react';

import { useBoolean, useLockFn } from 'ahooks';
import {
  Badge,
  Button,
  Divider,
  Form,
  Modal,
  notification,
  Select,
  Typography,
} from 'antd';
import { useNavigate } from 'react-router-dom';

import { CheckOutlinedIcon, DeleteOutlinedIcon } from 'components';
import { useProductCommentValue } from 'context';
import { deleteProductComment } from 'services';
import type { ProductCommentStatus } from 'types';
import { localizeProductCommentStatus, PATHNAME } from 'utils';

const {
  LOCAL: {
    PRODUCTS_COMMENTS: { BASE_URL: PRODUCTS_COMMENTS_BASE_URL },
  },
} = PATHNAME;

const productCommentStatuses = [
  'Accepted',
  'Rejected',
  'OnReview',
] satisfies ProductCommentStatus[];

type FieldType = Pick<ReturnType<typeof useProductCommentValue>, 'status'>;

const ProductCommentFormAction = () => {
  const [deleteModalInstance, deleteModalContextHolder] = Modal.useModal();
  const [deleteLoading, { toggle: toggleDeleteLoading }] = useBoolean();
  const { id, defaultStatus, mode } = useProductCommentValue();
  const navigate = useNavigate();

  const handleDeleteProductComment = useLockFn(
    async ({
      id,
    }: Pick<Required<ReturnType<typeof useProductCommentValue>>, 'id'>) => {
      toggleDeleteLoading();

      deleteProductComment({ id })
        .then(() => {
          navigate(PRODUCTS_COMMENTS_BASE_URL);
          notification.success({
            message: 'دیدگاه با موفقیت حذف شد.',
          });
        })
        .catch(message => {
          notification.error({
            message,
          });
        })
        .finally(() => {
          toggleDeleteLoading();
        });
    },
  );

  const { label: defaultStatusLabel, color: defaultStatusColor } =
    localizeProductCommentStatus(defaultStatus);

  return (
    <div>
      <div className='px-6 pt-4'>
        <Typography.Title level={3} className='mb-0 text-base font-medium'>
          انتشار دیدگاه
        </Typography.Title>
      </div>

      <Divider className='mb-8 mt-4' />

      <div className='px-6'>
        <Typography.Paragraph>
          <Badge
            text={`وضعیت فعلی: ${defaultStatusLabel}`}
            color={defaultStatusColor}
          />
        </Typography.Paragraph>
        <Form.Item<FieldType> label='وضعیت جدید' name='status' className='mb-2'>
          <Select
            allowClear
            placeholder='وضعیت جدید دیدگاه را انتخاب کنید'
            options={productCommentStatuses.map(status => {
              const { label } = localizeProductCommentStatus(status);
              return {
                label,
                value: status,
              };
            })}
          />
        </Form.Item>
      </div>

      <div className='flex flex-wrap-reverse items-center gap-2 p-6 *:m-0 *:shrink *:grow *:basis-40'>
        {mode === 'Update' ? (
          <Form.Item>
            <Button
              block
              danger
              className='flex items-center'
              loading={deleteLoading}
              icon={<DeleteOutlinedIcon />}
              onClick={async () => {
                const confirmed = await deleteModalInstance.confirm({
                  title: 'آیا از حذف این دیدگاه اطمینان دارید؟',
                  width: 500,
                });
                if (id && confirmed) {
                  handleDeleteProductComment({ id });
                }
              }}
            >
              <span>حذف دیدگاه</span>
            </Button>
            {deleteModalContextHolder}
          </Form.Item>
        ) : null}

        <Form.Item>
          <Button
            block
            type='primary'
            htmlType='submit'
            icon={<CheckOutlinedIcon />}
          >
            {mode === 'Create' ? (
              <span>افزودن دیدگاه جدید</span>
            ) : (
              <span>ذخیره تغییرات</span>
            )}
          </Button>
        </Form.Item>
      </div>
    </div>
  );
};

export { ProductCommentFormAction };
